import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CreateRecordWizard,
  CreateWizardBasics,
  CreateWizardClientPicker,
} from '../../components/create/CreateRecordWizard';
import ErrorBanner from '../../components/ErrorBanner';
import CustomerWorkIntakeForm from '../../components/create/CustomerWorkIntakeForm';
import {
  fetchClientBasics,
  fetchClientBranchDetails,
  type ClientBasicRecord,
  type ClientBranchDetail,
} from '../../data/clients';
import type { ClientSummary } from '../../domain/clients';
import { searchVenues, type VenueOption } from '../../data/eventWizard';
import { fetchVenueDetail } from '../../data/venues';
import {
  createInstallationJob,
  fetchClientContacts,
  installationErrorMessage,
  updateInstallationJob,
  type InstallationJobDetail,
  type InstallationLocationKind,
} from '../../data/installations';
import './Installation.css';

type AddressDraft = {
  label: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

const EMPTY_ADDRESS: AddressDraft = {
  label: 'Other address',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'USA',
};

function clientAddress(client: ClientBasicRecord | null, summary: ClientSummary | null): AddressDraft {
  return {
    label: summary?.businessName || client?.business_name || 'Client main address',
    line1: client?.address_line1 || '',
    line2: client?.address_line2 || '',
    city: client?.city || summary?.city || '',
    state: client?.state || summary?.state || '',
    postalCode: client?.postal_code || '',
    country: client?.country || '',
  };
}

function branchAddress(branch: ClientBranchDetail): AddressDraft {
  return {
    label: branch.branch_name || 'Client location',
    line1: branch.shipping_address_line1 || branch.billing_address_line1 || '',
    line2: branch.shipping_address_line2 || branch.billing_address_line2 || '',
    city: branch.shipping_city || branch.billing_city || '',
    state: branch.shipping_state || branch.billing_state || '',
    postalCode: branch.shipping_postal_code || branch.billing_postal_code || '',
    country: branch.shipping_country || branch.billing_country || '',
  };
}

function addressLines(address: AddressDraft): string[] {
  const cityState = [address.city, address.state].filter(Boolean).join(', ');
  const cityLine = [cityState, address.postalCode].filter(Boolean).join(' ');
  return [address.line1, address.line2, cityLine, address.country].filter(Boolean);
}

function addressOptionLabel(address: AddressDraft): string {
  const summary = addressLines(address).join(', ');
  return summary ? `${address.label} - ${summary}` : `${address.label} - Address not set`;
}

function addressIsUsable(address: AddressDraft): boolean {
  return Boolean(address.line1.trim() && address.city.trim() && address.state.trim());
}

type InstallationWizardPageProps = {
  onCancel: () => void;
  onCreated?: (jobId: string) => void;
  onSaved?: () => void;
  job?: InstallationJobDetail | null;
};

function InstallationDetailsWizard({ onCancel, onCreated, onSaved, job = null }: InstallationWizardPageProps) {
  const editing = Boolean(job);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [name, setName] = React.useState(job?.name ?? '');
  const [selectedClient, setSelectedClient] = React.useState<ClientSummary | null>(() => job?.clientId ? ({
    id: job.clientId,
    businessName: job.clientName || 'Current client',
    taxStatus: null,
  }) : null);
  const [contactId, setContactId] = React.useState(job?.primaryContactId ?? '');
  const [locationChoice, setLocationChoice] = React.useState(() => job?.locationKind === 'branch' && job.clientBranchId
    ? `branch:${job.clientBranchId}`
    : job?.locationKind === 'venue'
      ? 'venue'
      : job?.locationKind === 'other'
        ? 'other'
        : 'main');
  const [venueSearch, setVenueSearch] = React.useState(job?.venueName ?? '');
  const [selectedVenue, setSelectedVenue] = React.useState<VenueOption | null>(() => job?.venueId ? ({
    id: job.venueId,
    venue_name: job.venueName || 'Current venue',
    city: job.address.city,
    state: job.address.state,
  }) : null);
  const [customAddress, setCustomAddress] = React.useState<AddressDraft>(() => job ? ({
    label: 'Other address',
    line1: job.address.line1,
    line2: job.address.line2,
    city: job.address.city,
    state: job.address.state,
    postalCode: job.address.postalCode,
    country: job.address.country,
  }) : ({ ...EMPTY_ADDRESS }));
  const [requestedStartDate, setRequestedStartDate] = React.useState(job?.requestedStartDate ?? '');
  const clientId = selectedClient?.id ?? '';

  const clientBasicsQuery = useQuery({
    queryKey: ['installation-create-client-basics', clientId],
    queryFn: async () => {
      const result = await fetchClientBasics(clientId, { includeAccountStatus: false });
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: Boolean(clientId),
  });
  const clientBranchesQuery = useQuery({
    queryKey: ['installation-create-client-branches', clientId],
    queryFn: async () => {
      const result = await fetchClientBranchDetails(clientId);
      if (result.error) throw result.error;
      return result.data.branches;
    },
    enabled: Boolean(clientId),
  });
  const contactsQuery = useQuery({
    queryKey: ['installation-client-contacts', clientId],
    queryFn: () => fetchClientContacts(clientId),
    enabled: Boolean(clientId),
  });
  const venueSearchQuery = useQuery({
    queryKey: ['installation-create-venues', venueSearch],
    queryFn: async () => {
      const result = await searchVenues(venueSearch.trim());
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: locationChoice === 'venue',
  });
  const venueDetailQuery = useQuery({
    queryKey: ['installation-create-venue-detail', selectedVenue?.id],
    queryFn: async () => {
      if (!selectedVenue) return null;
      const result = await fetchVenueDetail(selectedVenue.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: locationChoice === 'venue' && Boolean(selectedVenue),
  });

  const mainAddress = React.useMemo(
    () => clientAddress(clientBasicsQuery.data ?? null, selectedClient),
    [clientBasicsQuery.data, selectedClient],
  );
  const branchAddresses = React.useMemo(
    () => (clientBranchesQuery.data ?? []).map(branch => ({ id: branch.id, address: branchAddress(branch) })),
    [clientBranchesQuery.data],
  );
  const selectedAddress = React.useMemo(() => {
    if (locationChoice === 'other') return customAddress;
    if (locationChoice === 'venue') {
      const venue = venueDetailQuery.data;
      return {
        label: venue?.venue_name || selectedVenue?.venue_name || 'Selected venue',
        line1: venue?.address_line1 || '',
        line2: venue?.address_line2 || '',
        city: venue?.city || selectedVenue?.city || '',
        state: venue?.state || selectedVenue?.state || '',
        postalCode: venue?.postal_code || '',
        country: venue?.country || '',
      };
    }
    if (locationChoice.startsWith('branch:')) {
      return branchAddresses.find(item => `branch:${item.id}` === locationChoice)?.address ?? mainAddress;
    }
    return mainAddress;
  }, [branchAddresses, customAddress, locationChoice, mainAddress, selectedVenue, venueDetailQuery.data]);
  const addressReady = addressIsUsable(selectedAddress);
  const createDisabled = busy
    || !name.trim()
    || !selectedClient
    || !contactId
    || !addressReady;

  function selectClient(client: ClientSummary | null) {
    setSelectedClient(client);
    setContactId('');
    setLocationChoice('main');
    setSelectedVenue(null);
    setVenueSearch('');
    setCustomAddress({ ...EMPTY_ADDRESS });
    setError(null);
  }

  function updateCustomAddress(field: keyof AddressDraft, value: string) {
    setCustomAddress(current => ({ ...current, [field]: value }));
  }

  async function handleCreate() {
    if (createDisabled || !selectedClient || !contactId) return;
    setBusy(true);
    setError(null);
    try {
      const locationKind: InstallationLocationKind = locationChoice === 'venue'
        ? 'venue'
        : locationChoice === 'other'
          ? 'other'
          : locationChoice.startsWith('branch:')
            ? 'branch'
            : 'client';
      const shared = {
        name: name.trim(),
        client_id: selectedClient.id,
        primary_contact_id: contactId,
        client_branch_id: locationChoice.startsWith('branch:') ? Number(locationChoice.slice('branch:'.length)) : null,
        venue_id: locationChoice === 'venue' ? selectedVenue?.id ?? null : null,
        installation_location_kind: locationKind,
        requested_start_date: requestedStartDate || null,
        installation_address_line1: selectedAddress.line1.trim(),
        installation_address_line2: selectedAddress.line2.trim(),
        installation_city: selectedAddress.city.trim(),
        installation_state: selectedAddress.state.trim(),
        installation_postal_code: selectedAddress.postalCode.trim(),
        installation_country: selectedAddress.country.trim(),
      };
      if (job) {
        await updateInstallationJob(job.id, job.version, shared);
        onSaved?.();
      } else {
        const jobId = await createInstallationJob(shared);
        onCreated?.(jobId);
      }
    } catch (caught) {
      setError(installationErrorMessage(caught, 'Installation quote could not be created.'));
    } finally {
      setBusy(false);
    }
  }

  const queryError = clientBasicsQuery.error || clientBranchesQuery.error || contactsQuery.error || venueSearchQuery.error || venueDetailQuery.error;
  const selectedAddressLines = addressLines(selectedAddress);

  return (
    <CreateRecordWizard
      title={editing ? 'Edit Installation Job' : 'Create Installation Quote'}
      className="installation-create-wizard"
      bodyClassName="installation-create-wizard__body"
      footer={(
        <>
          <button type="button" className="btn-close" onClick={onCancel} disabled={busy} aria-label="Cancel">&times;</button>
          <button type="submit" form="installation-create-form" className="btn btn-primary" disabled={createDisabled}>
            {busy ? (editing ? 'Saving...' : 'Creating...') : (editing ? 'Save changes' : 'Create quote')}
          </button>
        </>
      )}
    >
      <form
        id="installation-create-form"
        className="installation-create-form"
        onSubmit={event => { event.preventDefault(); void handleCreate(); }}
        aria-busy={busy}
      >
        {error || queryError ? (
          <ErrorBanner message={error ?? installationErrorMessage(queryError)} />
        ) : null}

        <CreateWizardBasics className="installation-create-grid">
            <label className="installation-create-field installation-create-field--name">
              <span>Installation name *</span>
              <input
                className="input"
                value={name}
                onChange={event => setName(event.target.value)}
                placeholder="Executive conference room upgrade"
                autoFocus
                required
                disabled={busy}
              />
            </label>

            <label className="installation-create-field installation-create-field--date">
              <span>Requested start date</span>
              <input
                className="input"
                type="date"
                value={requestedStartDate}
                onChange={event => setRequestedStartDate(event.target.value)}
                disabled={busy}
              />
            </label>

            <div className="installation-create-field installation-create-field--client">
              <CreateWizardClientPicker
                selectedClient={selectedClient}
                onChange={selectClient}
                required
                disabled={busy}
                helperText="Choose the client receiving this quote."
              />
            </div>

            <label className="installation-create-field installation-create-field--contact">
              <span>Primary quote contact *</span>
              <select
                className="input"
                value={contactId}
                onChange={event => setContactId(event.target.value)}
                required
                disabled={busy || !selectedClient || contactsQuery.isLoading}
              >
                <option value="">
                  {!selectedClient
                    ? 'Select a client first'
                    : contactsQuery.isLoading
                      ? 'Loading contacts...'
                      : 'Select a quote contact...'}
                </option>
                {contactsQuery.data?.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {contact.label}{contact.detail ? ` - ${contact.detail}` : ''}
                  </option>
                ))}
              </select>
              <small className="fg-muted">The client contact for this quote. Add billing and on-site contacts with their roles in the Contacts tab.</small>
            </label>

            <div className="installation-create-field installation-create-field--location">
              <label htmlFor="installation-create-location">Installation location *</label>
              <select
                id="installation-create-location"
                className="input"
                value={locationChoice}
                onChange={event => setLocationChoice(event.target.value)}
                disabled={busy || !selectedClient || clientBasicsQuery.isLoading || clientBranchesQuery.isLoading}
              >
                <option value="main">{selectedClient ? addressOptionLabel(mainAddress) : 'Select a client first'}</option>
                {branchAddresses.map(item => (
                  <option key={item.id} value={`branch:${item.id}`}>{addressOptionLabel(item.address)}</option>
                ))}
                <option value="venue">Venue address</option>
                <option value="other">Other address</option>
              </select>
              <span className="installation-create-helper">Choose the client, a saved branch, a venue, or another job-site address.</span>

              {locationChoice === 'venue' ? (
                <div className="installation-create-venue-picker">
                  <label className="installation-create-field">
                    <span>Find venue *</span>
                    <input
                      className="input"
                      type="search"
                      value={venueSearch}
                      onChange={event => { setVenueSearch(event.target.value); setSelectedVenue(null); }}
                      placeholder="Search venue name"
                      disabled={busy}
                    />
                  </label>
                  <div className="installation-create-venue-results" role="listbox" aria-label="Venue results">
                    {(venueSearchQuery.data ?? []).map(venue => (
                      <button
                        key={venue.id}
                        type="button"
                        className="installation-create-venue-option"
                        data-selected={selectedVenue?.id === venue.id || undefined}
                        onClick={() => { setSelectedVenue(venue); setVenueSearch(venue.venue_name); }}
                      >
                        <strong>{venue.venue_name}</strong>
                        <span>{[venue.city, venue.state].filter(Boolean).join(', ') || 'Address not set'}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {locationChoice === 'other' ? (
                <div className="installation-create-address-grid">
                  <label className="installation-create-field installation-create-address-field--line1">
                    <span>Address line 1 *</span>
                    <input className="input" value={customAddress.line1} onChange={event => updateCustomAddress('line1', event.target.value)} required disabled={busy} />
                  </label>
                  <label className="installation-create-field installation-create-address-field--line2">
                    <span>Address line 2</span>
                    <input className="input" value={customAddress.line2} onChange={event => updateCustomAddress('line2', event.target.value)} disabled={busy} />
                  </label>
                  <label className="installation-create-field installation-create-address-field--city">
                    <span>City *</span>
                    <input className="input" value={customAddress.city} onChange={event => updateCustomAddress('city', event.target.value)} required disabled={busy} />
                  </label>
                  <label className="installation-create-field installation-create-address-field--state">
                    <span>State *</span>
                    <input className="input" value={customAddress.state} onChange={event => updateCustomAddress('state', event.target.value)} required disabled={busy} />
                  </label>
                  <label className="installation-create-field installation-create-address-field--postal">
                    <span>Postal code</span>
                    <input className="input" value={customAddress.postalCode} onChange={event => updateCustomAddress('postalCode', event.target.value)} disabled={busy} />
                  </label>
                  <label className="installation-create-field installation-create-address-field--country">
                    <span>Country</span>
                    <input className="input" value={customAddress.country} onChange={event => updateCustomAddress('country', event.target.value)} disabled={busy} />
                  </label>
                </div>
              ) : (
                <address className="installation-create-address-preview">
                  <strong>{selectedAddress.label}</strong>
                  {selectedAddressLines.length
                    ? <span>{selectedAddressLines.join(', ')}</span>
                    : <span>Address not set on the client record.</span>}
                </address>
              )}

              {selectedClient && !addressReady ? (
                <p className="installation-create-location__feedback" role="alert">
                  Choose Other address and enter the street, city, and state before creating the quote.
                </p>
              ) : null}
            </div>
        </CreateWizardBasics>
      </form>
    </CreateRecordWizard>
  );
}

export default function InstallationWizardPage(props: InstallationWizardPageProps) {
  return props.job ? <InstallationDetailsWizard {...props} /> : <CustomerWorkIntakeForm installation={{ onCancel: props.onCancel, onCreated: props.onCreated }} />;
}
